import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { ErrorHandlerService } from '@y42/error-handling';
import { ConflictSide, contentHasConflicts, GitConflict, GitQuery, GitService, resolveConflict } from '@y42/git';
import { ToastService } from '@y42/toast';
import { BehaviorSubject, map, switchMap, tap } from 'rxjs';
import { GitDiffTreeComponent } from '../git-diff-tree';

export const CONFLICTS_DIALOG_ID = 'conflicts-dialog';

@UntilDestroy()
@Component({
  selector: 'y42-git-conflicts-dialog',
  templateUrl: './git-conflicts-dialog.component.html',
  styleUrls: ['./git-conflicts-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GitConflictsDialogComponent {
  constructor(
    private readonly gitService: GitService,
    public dialogRef: MatDialogRef<GitConflictsDialogComponent>,
    private readonly toast: ToastService,
    private readonly gitQuery: GitQuery,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
  ) {}

  @ViewChild(GitDiffTreeComponent) gitDiffTreeComponent!: GitDiffTreeComponent;

  private readonly reloadTrigger$ = new BehaviorSubject(null);

  private readonly resolving$$ = new BehaviorSubject<null | 'theirs' | 'yours'>(null);

  readonly state = connectState(this, {
    conflictType: this.gitQuery.gitStatus$.pipe(map((status) => status?.params as string)),
    conflicts: this.reloadTrigger$.pipe(
      switchMap(() => this.gitService.getFileConflicts()),
      tap((conflicts) => {
        const conflict = conflicts[0];
        if (conflict) {
          this.selectActiveConflict(conflict);
        } else {
          this.continueAfterConflicts();
        }
      }),
    ),
    resolving: this.resolving$$,
  });

  readonly mergeModeFC = new FormControl<'simple' | 'vscode'>('simple', { nonNullable: true });

  committing$ = new BehaviorSubject(false);

  activeConflict: GitConflict | null = null;

  selectedConflicts: GitConflict[] = [];

  async resolveConflict(chosenContent: 'yours' | 'theirs') {
    this.resolving$$.next(chosenContent);
    try {
      this.selectedConflicts.forEach(preResolveConflict);
      await this.gitService.resolveConflicts(this.selectedConflicts, chosenContent);
      this.reloadTrigger$.next(null);
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      console.error(error);
    } finally {
      this.resolving$$.next(null);
    }
  }

  completeMerge() {
    this.reloadTrigger$.next(null);
  }

  selectActiveConflict(conflict: GitConflict | null) {
    this.activeConflict = conflict;
    if (!conflict) {
      return;
    }
    preResolveConflict(conflict);
  }

  private async continueAfterConflicts() {
    this.committing$.next(true);
    try {
      await this.gitService.createMergeCommit();
      this.toast.info('Your merge has been successful');
      this.dialogRef.close();
      // perform "renavigation" to the same route
      // so that on initial load user can perfectly complete git initialization
      this.router.navigateByUrl(location.pathname);
    } catch (err) {
      this.toast.error(err instanceof Error ? err.message : `${err}`);
    } finally {
      this.committing$.next(false);
    }
  }
}

function preResolveConflict(conflict: GitConflict) {
  const { content } = conflict;
  if (contentHasConflicts(content)) {
    conflict.theirs = resolveConflict(content, ConflictSide.THEIRS);
    conflict.yours = resolveConflict(content, ConflictSide.YOURS);
  }
}
